import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup } from '@angular/forms';
import { AgGridAngular } from 'ag-grid-angular';
import {
    CellClassRules,
    CellEditingStoppedEvent,
    CellSelectionOptions,
    CellValueChangedEvent,
    ColDef,
    GetRowIdParams,
    GridApi,
    GridReadyEvent,
    SizeColumnsToContentStrategy,
    ValueSetterParams
} from 'ag-grid-community';
import {
    WorkflowConfigField,
    WorkflowConfigFieldSelect,
    WorkflowConfigFieldType,
    WorkflowData
} from '@monorepo/interfaces';
import { AgGridLocaleService } from '../../../core/ag-grid-locale.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ControlErrorHandlerPipe, ControlErrorParamsPipe } from '../../pipes/control-error-handler.pipe';
import { Store } from '@ngxs/store';
import { WorkflowDataState } from '../../state/workflow/workflow-data.state';
import { ProductRowData } from './product-table.interface';
import {
    CORE_MLFB_COL_DEF,
    DEFAULT_COL_DEF,
    FILL_HANDLE_CELL_SELECTION,
    MAX_PRODUCTS,
    getCellEditorConfig
} from './product-table.config';
import { map } from 'rxjs';

@Component({
    selector: 'app-product-table',
    imports: [AgGridAngular, TranslatePipe],
    templateUrl: './product-table.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductTableComponent {
    private readonly store = inject(Store);
    private readonly agGridLocaleService = inject(AgGridLocaleService);
    private readonly translateService = inject(TranslateService);
    private readonly controlErrorHandlerPipe = new ControlErrorHandlerPipe();
    private readonly controlErrorParamsPipe = new ControlErrorParamsPipe();

    public readonly productFields = input.required<WorkflowConfigField[]>();
    public readonly productsForm = input.required<FormGroup>();
    public readonly stepForm = input.required<FormGroup>();
    public readonly disabled = input.required<boolean>();

    private readonly workflowData = this.store.selectSignal<WorkflowData | null>(WorkflowDataState.selectedWorkflow);
    protected readonly products = computed(() => this.workflowData()?.products ?? []);

    private gridApi: GridApi | null = null;

    private readonly currentLang = toSignal(this.translateService.onLangChange.pipe(map(event => event.lang)), {
        initialValue: this.translateService.getCurrentLang()
    });

    protected readonly localeText = this.agGridLocaleService.localeText;
    protected readonly maxProducts = MAX_PRODUCTS;
    protected readonly popupParent = document.body;
    protected readonly defaultColDef = DEFAULT_COL_DEF;
    protected readonly autoSizeStrategy: SizeColumnsToContentStrategy = { type: 'fitCellContents' };
    protected readonly getRowId = (params: GetRowIdParams<ProductRowData>) => params.data.productName;

    protected readonly rowData = computed(() => this.buildRowData());

    private readonly fieldsByName = computed(() => {
        const map = new Map<string, WorkflowConfigField>();
        this.productFields().forEach(f => map.set(f.name, f));
        return map;
    });

    protected readonly columnDefs = computed(() => {
        const fields = this.productFields();
        const disabled = this.disabled();
        // Track language changes to rebuild column headers with fresh translations
        this.currentLang();
        if (!fields?.length) return [];

        const coreMlfbCol: ColDef = {
            ...CORE_MLFB_COL_DEF,
            headerName: this.translateService.instant('field-core-mlfb')
        };

        return [coreMlfbCol, ...fields.map(field => this.createFieldColDef(field, disabled))];
    });

    protected readonly cellSelection = computed<boolean | CellSelectionOptions>(() => {
        if (this.disabled()) return false;
        return FILL_HANDLE_CELL_SELECTION;
    });

    protected onGridReady(event: GridReadyEvent): void {
        this.gridApi = event.api;
    }

    protected onFirstDataRendered(): void {
        this.gridApi?.autoSizeAllColumns();
    }

    protected onCellValueChanged(event: CellValueChangedEvent<ProductRowData>): void {
        const fieldName = event.colDef.field;
        if (!fieldName || fieldName === 'coreMlfb') return;

        const stringValue = event.newValue != null && event.newValue !== '' ? String(event.newValue) : '';

        const control = this.getProductFieldControl(event.data.productName, fieldName);
        if (control) {
            control.setValue(stringValue);
            control.markAsTouched();
            control.markAsDirty();

            // Validate pasted values against allowed options for SELECT fields
            const fieldConfig = this.fieldsByName().get(fieldName);
            if (fieldConfig?.fieldType === WorkflowConfigFieldType.SELECT) {
                const options = (fieldConfig as WorkflowConfigFieldSelect).options ?? [];
                if (stringValue !== '' && options.length > 0 && !options.some(opt => String(opt) === stringValue)) {
                    control.setErrors({ ...control.errors, invalidOption: true });
                }
            }
        }

        // Sync to step-level control so dependent field subscriptions fire
        const stepControl = this.stepForm()?.get(fieldName);
        if (stepControl instanceof FormControl) {
            stepControl.setValue(stringValue, { emitEvent: true });
        }

        // Force re-evaluation of cellClassRules so validation styling appears immediately
        event.api.refreshCells({
            rowNodes: [event.node],
            columns: [event.column],
            force: true
        });
    }

    protected onCellEditingStopped(event: CellEditingStoppedEvent<ProductRowData>): void {
        if (event.rowIndex == null || !event.column) return;

        event.api.clearCellSelection();
        event.api.addCellRange({
            rowStartIndex: event.rowIndex,
            rowEndIndex: event.rowIndex,
            columns: [event.column]
        });
    }

    private buildRowData(): ProductRowData[] {
        const products = this.products();
        const fields = this.productFields();
        if (!products?.length || !fields?.length) return [];

        return products.slice(0, MAX_PRODUCTS).map(product => {
            const row: ProductRowData = { productName: product.name, coreMlfb: product.name };
            fields.forEach(field => {
                const control = this.getProductFieldControl(product.name, field.name);
                row[field.name] = control?.value ?? '';
            });
            return row;
        });
    }

    private createFieldColDef(field: WorkflowConfigField, disabled: boolean): ColDef {
        const invalidCellClassRules: CellClassRules = {
            'cell-invalid': params => {
                const control = this.getProductFieldControl(params.data?.productName, field.name);
                return !!control?.invalid && !!control?.touched;
            }
        };

        const colDef: ColDef = {
            headerName: (field.required ? '* ' : '') + this.translateService.instant(field.label),
            headerTooltipValueGetter: field.hint ? () => this.translateService.instant(field.hint) : undefined,
            field: field.name,
            editable: params => {
                if (disabled) return false;
                const control = this.getProductFieldControl(params.data?.productName, field.name);
                return control ? !control.disabled : false;
            },
            cellClassRules: invalidCellClassRules,
            valueSetter: (params: ValueSetterParams<ProductRowData>) => {
                const fieldName = params.colDef.field;
                if (!fieldName) return false;
                params.data[fieldName] = params.newValue;
                return true;
            },
            tooltipValueGetter: params => {
                const control = this.getProductFieldControl(params.data?.productName, field.name);
                if (control?.invalid && control?.touched) {
                    const key = this.controlErrorHandlerPipe.transform(control.errors, true);
                    const params = this.controlErrorParamsPipe.transform(control.errors, true);
                    return key ? this.translateService.instant(key, params) : '';
                }
                return '';
            }
        };

        return { ...colDef, ...getCellEditorConfig(field) };
    }

    private getProductFieldControl(productName: string, fieldName: string): FormControl | null {
        const productGroup = this.productsForm()?.get(`product-${productName}`) as FormGroup;
        const control = productGroup?.get(fieldName);
        return control instanceof FormControl ? control : null;
    }
}
